import debounce from 'lodash/debounce';
import { FC, PropsWithChildren, useCallback, useEffect, useMemo, useRef } from 'react';

interface InfiniteScrollWrapperProps {
  loading: boolean;
  loadNext: () => void;
}

const DEBOUNCE_DELAY_MS = 150;

export const InfiniteScrollWrapper: FC<PropsWithChildren<InfiniteScrollWrapperProps>> = props => {
  const { children, loading, loadNext } = props;
  const scrollListener = useRef(loadNext);
  const contentRef = useRef<HTMLDivElement>(null);
  const loadingRef = useRef(loading);

  useEffect(() => {
    scrollListener.current = loadNext;
  }, [loadNext]);

  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  // Create a stable debounced function that calls the latest loadNext
  const debouncedLoadNext = useMemo(
    () =>
      debounce(() => {
        if (!loadingRef.current) {
          scrollListener.current();
        }
      }, DEBOUNCE_DELAY_MS),
    []
  );

  // Cleanup debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedLoadNext.cancel();
    };
  }, [debouncedLoadNext]);

  const onScroll = useCallback(() => {
    const loadPoint =
      (contentRef.current?.offsetHeight ?? 0) + (contentRef.current?.offsetTop ?? 0) - window.innerHeight;
    const scrollDifference = Math.floor(window.innerHeight + window.scrollY);
    const scrollEnded = scrollDifference > loadPoint;

    if (scrollEnded && !loadingRef.current) {
      debouncedLoadNext();
    }
  }, [debouncedLoadNext]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.addEventListener('scroll', onScroll);
    }
    onScroll();
    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, [onScroll]);

  return <div ref={contentRef}>{children}</div>;
};
